/**
 * Switcher de GLN operativo — botón en el header con dropdown.
 *
 * Habilitado solo si el usuario tiene 2+ GLNs permitidos (`puedeActuar
 * switch_location`). El cambio de GLN se hace directamente, sin step-up
 * auth — es un cambio de CONTEXTO, no una operación GxP.
 *
 * Si en el futuro Compliance pide step-up local (Part 11 §11.200 "two
 * distinct components"), agregamos PIN por usuario y lo pedimos acá.
 * El cambio queda auditado igualmente.
 *
 * Las ubicaciones externas (`externa: true`) JAMÁS aparecen en la lista —
 * filtradas por `getUbicacionesPermitidas` desde el domain.
 */

import { ChevronDown, Check, Star } from 'lucide-react';
import { toast } from '@/lib/notify';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useSessionStore } from '@/stores/session';
import { useUbicaciones, useSetDefaultGlnMutation } from '@/api/queries';
import { useAuditStore } from '@/stores/audit';
import { puedeActuar, getUbicacionesPermitidas } from '@/domain/rbac';

export function LocationSwitcher() {
  const usuario = useSessionStore((s) => s.usuario);
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const switchUbicacion = useSessionStore((s) => s.switchUbicacion);
  const setUbicacionDefault = useSessionStore((s) => s.setUbicacionDefault);
  const { data: ubicaciones = [] } = useUbicaciones();
  const addAuditEntry = useAuditStore((s) => s.addEntry);
  const setDefaultMutation = useSetDefaultGlnMutation();
  const queryClient = useQueryClient();

  const handleSetDefault = (ub: { id: string; nombre: string }, e: React.MouseEvent) => {
    // El click en la estrella no debe cerrar el dropdown ni cambiar el GLN
    // activo — son operaciones distintas (default vs activo).
    e.preventDefault();
    e.stopPropagation();
    if (!usuario || (usuario.ubicacionDefaultId ?? usuario.ubicacionId) === ub.id) return;

    setDefaultMutation.mutate(ub.id, {
      onSuccess: () => {
        setUbicacionDefault(ub.id);
        toast.success(`${ub.nombre} marcado como GLN por default`);
        addAuditEntry({
          categoria: 'sesion',
          accion: 'set_default_location',
          razon: 'Cambio del GLN default del usuario',
          valoresDespues: { ubicacionId: ub.id, ubicacionNombre: ub.nombre },
        });
      },
      onError: (err) => {
        toast.error(`No se pudo cambiar el default: ${err.message}`);
      },
    });
  };

  if (!usuario || ubicaciones.length === 0) return null;

  const permitidas = getUbicacionesPermitidas(usuario, ubicaciones);
  const activa = ubicaciones.find((u) => u.id === ubicacionActivaId);
  const puedeCambiar = puedeActuar(usuario, 'switch_location', ubicaciones);

  const handleSelect = (ubId: string) => {
    if (ubId === ubicacionActivaId) return;

    const ubAntes = activa;
    const ubDestino = ubicaciones.find((u) => u.id === ubId);
    if (!ubDestino) return;

    const ok = switchUbicacion(ubId, ubicaciones);
    if (!ok) {
      toast.error('No tenés permiso para operar en ese GLN');
      addAuditEntry({
        categoria: 'sesion',
        accion: 'permiso.denegado',
        razon: `Cambio a GLN no permitido · ${ubId}`,
      });
      return;
    }

    addAuditEntry({
      categoria: 'sesion',
      accion: 'switch_location',
      razon: 'Cambio de GLN operativo (cambio de contexto, sin firma)',
      valoresAntes: { ubicacionId: ubAntes?.id, ubicacionNombre: ubAntes?.nombre },
      valoresDespues: { ubicacionId: ubDestino.id, ubicacionNombre: ubDestino.nombre },
    });
    // Invalidamos las queries que dependen del GLN — sus queryKeys ya
    // incluyen el glnId, pero invalidar fuerza un refetch fresco aún si
    // la query del nuevo glnId ya estaba en cache.
    void queryClient.invalidateQueries({ queryKey: ['erp-tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['traza-stats'] });
    toast.success(`Cambiado a ${ubDestino.nombre}`);
  };

  // Si no puede cambiar (un solo GLN), mostrar como label estático
  if (!puedeCambiar) {
    return (
      <div
        className="text-left leading-tight"
        title="Tu usuario tiene un único GLN operativo asignado"
      >
        <div className="text-xs text-gray-600">Operando como:</div>
        <div className="text-sm">
          <span className="font-bold text-everex-azul">{activa?.nombre ?? '—'}</span>
          <span className="ml-1 text-xs text-gray-600">(GLN {activa?.gln ?? '—'})</span>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded text-left leading-tight transition hover:opacity-80">
        <div>
          <div className="text-xs text-gray-600">Operando como:</div>
          <div className="text-sm">
            <span className="font-bold text-everex-azul">{activa?.nombre ?? '—'}</span>
            <span className="ml-1 text-xs text-gray-600">(GLN {activa?.gln ?? '—'})</span>
          </div>
        </div>
        <ChevronDown className="h-4 w-4 text-gray-600" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[280px]">
        <DropdownMenuLabel className="text-[11px] uppercase text-gray-600">
          GLNs operables
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {permitidas.map((u) => {
          const isActive = u.id === ubicacionActivaId;
          const isDefault = (usuario.ubicacionDefaultId ?? usuario.ubicacionId) === u.id;
          const isPending =
            setDefaultMutation.isPending && setDefaultMutation.variables === u.id;
          return (
            <DropdownMenuItem
              key={u.id}
              onSelect={() => handleSelect(u.id)}
              className={cn('group cursor-pointer', isActive && 'bg-everex-azul-bg')}
            >
              <div className="flex w-full items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 text-sm font-bold text-everex-azul">
                    {u.nombre}
                    {/* Default actual → estrella llena, no clickable.
                        Resto → estrella outline (visible solo en hover del item)
                        que al click setea como default. */}
                    {isDefault ? (
                      <Star
                        className="h-3 w-3 fill-gray-400 text-gray-600"
                        aria-label="GLN por default — se setea al iniciar sesión"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleSetDefault(u, e)}
                        disabled={isPending}
                        title="Marcar como GLN por default"
                        className={cn(
                          'rounded p-0.5 opacity-0 transition hover:bg-gray-100 group-hover:opacity-100',
                          isPending && 'opacity-100',
                        )}
                      >
                        <Star
                          className={cn(
                            'h-3 w-3 text-gray-400 hover:text-yellow-500',
                            isPending && 'animate-pulse text-yellow-500',
                          )}
                          aria-label="Marcar como GLN por default"
                        />
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-gray-600">{u.tipo ?? u.tipoActor}</div>
                  <div className="text-[11px] text-gray-600">GLN {u.gln}</div>
                </div>
                {isActive && <Check className="h-5 w-5 shrink-0 text-everex-naranja" />}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
